import Button from '@/features/shared/Button';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { datalayerPush, withGTM } from '@/features/shared/HOCs/withGTM';
import { MuiTypography } from '@/features/shared/material-ui';
import { DEFAULT_PRINT_STYLE } from '@/utils/constants';
import CloseIcon from '@material-ui/icons/Close';
import PictureAsPdf from '@material-ui/icons/PictureAsPdf';
import clsx from 'clsx';
import React, { memo, useMemo, useRef } from 'react';
import TagManager from 'react-gtm-module';
import ReactToPrint from 'react-to-print';
import { getMakeAndModel, onDatalayerCustom } from '../SDL.datalayer';
import { SDLdetailProps } from './SDLdetail.interface';
import useStyles from './SDLdetail.styles';
import { getDeviceType } from './SDLdetail.utils';
import AdditionalInformation from './shared/Additional';
import BasicInformation from './shared/Basic';
import FirmwareInformation from './shared/Firmware';

const SDL_ENGINES = [
  'sdl-camera',
  'dev-sdl-camera',
  'sdl-stratocast',
  'dev-sdl-stratocast',
  'sdl-intercom',
  'dev-sdl-intercom',
  'sdl-saas',
  'dev-sdl-saas',
];

const SDLdetailComponent = ({ content, modalClose, theme, labels }: SDLdetailProps) => {
  const { queryParams } = React.useContext(ElasticComponentContext);
  const classes = useStyles({});
  const tableRef = useRef<any>();

  const additionalSection = useMemo(
    () => !!SDL_ENGINES.find((category) => category === content[0]?._meta?.engine),
    [content],
  );

  const deviceType = getDeviceType(content[0]?._meta?.engine);
  const rootClasses = clsx(classes.root, `${theme}-theme`);
  const firmwareTitle = React.useMemo(
    () => labels[`detail_header_firmware_${deviceType}`] || labels?.detail_header_firmware,
    [deviceType, labels],
  );
  const variantName = (tableRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement)
    ?.dataset?.datalayerVariant;

  const dataLayerPrintDeviceDetail = React.useCallback(() => {
    datalayerPush({
      compRef: tableRef,
      args: {
        event: 'printPdf',
        variantName,
      },
      event: 'gtm:custom:sdl',
    });

    const dataLayer = {
      event: 'trackEvent-sdlPrintDeviceDetail',
      event_name: 'sdl_print_device_detail',
      position: 'sdl',
      sdl_category: queryParams?.category_type,
      sdl_detail_id: content?.[0]?.id?.raw,
      make_model: getMakeAndModel(content),
    };
    TagManager.dataLayer({ dataLayer });
  }, [content, queryParams?.category_type, variantName]);

  return (
    <div className={classes.wrapper} ref={tableRef}>
      <div className={rootClasses}>
        <Button className="close-modal" onClick={modalClose}>
          <CloseIcon fontSize="small" />
        </Button>
        <div className="sdl-detail-info-heading">
          <MuiTypography variant="h4" component="h4" role="heading" data-testid="sdl-title">
            {labels?.detail_device_details}
          </MuiTypography>
        </div>

        <div className={classes.section}>
          <section className="basic-info">
            <div className="basic-detail-heading">
              <MuiTypography variant="h5" component="h5" role="heading" data-testid="sdl-title">
                {labels?.detail_header_basic}
              </MuiTypography>
              <div>
                <ReactToPrint
                  pageStyle={DEFAULT_PRINT_STYLE}
                  onBeforeGetContent={dataLayerPrintDeviceDetail}
                  trigger={() => {
                    return (
                      <Button
                        className="gtm-print"
                        aria-haspopup="true"
                        outlined
                        endIcon={<PictureAsPdf />}
                      >
                        {labels?.detail_print}
                      </Button>
                    );
                  }}
                  content={() => tableRef.current}
                />
              </div>
            </div>
            <BasicInformation content={content} labels={labels} deviceType={deviceType} />
          </section>
        </div>

        {!additionalSection ? (
          <div className="spacer" />
        ) : (
          <div className={classes.section}>
            <section className="additional-info">
              <div className="additional-detail-heading">
                <MuiTypography variant="h5" component="h5" role="heading" data-testid="sdl-title">
                  {labels?.detail_header_additional}
                </MuiTypography>
              </div>
              <AdditionalInformation content={content} labels={labels} deviceType={deviceType} />
            </section>
          </div>
        )}

        <div className={classes.section}>
          <section className="firmware-info">
            <FirmwareInformation
              deviceType={deviceType}
              content={content}
              labels={labels}
              title={firmwareTitle}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

const SDLdetail = withGTM({
  Component: memo(SDLdetailComponent),
  onDatalayer: onDatalayerCustom,
  config: {
    eventName: 'gtm:custom:sdl',
    isCustom: true,
  },
});

export default SDLdetail;
